import { create, ApiResponse, ApisauceInstance } from 'apisauce';
import qs from 'qs';
import {
  UserInfo,
  User,
  Circulations,
  UpdatePasswordParams,
  PublicStation,
  StationsServed,
  Reason,
  Mask,
  MasksConfig,
  Typology,
  ProductCode,
  HelpInfo,
  CreateHelpInfoParams,
  ReasonCreationParams,
  HistoricCommunication,
  HistoricPendingCommunication,
  LatestCommunication,
  ServiceInfo,
  ApiVersion,
  HelpInfos,
  UpdateHelpInfoParams,
  Situations,
  MessageMask,
  CompensationSentence,
  CompensationSentenceParams,
  Profil,
  Section,
  UserModifyInfo,
  CreatedUser,
  ConflictType,
  LoginResponse,
  GetCirculationFilters,
  StationsTemplatesPreview,
  TypologyForm,
  SituationForm,
  Situation,
  InitializeCommunication,
  GetInitializeCommunication,
  ServicesPassengersConcernedByCommunicationParams,
  ServicesPassengersConcernedByCommunicationResponse,
  SendCommunicationFormValues,
  MinimalResponse,
  SendStatus,
  CommunicationsHistoryForm,
  FluxClientsFilters,
  CustomersByStation,
  CustomersByOD,
  Users,
  ProfilParams,
  HelpInfosParams,
  ServiceType,
  UserFilterParams,
  CommunicationsPendingHistoryForm,
  UserParams,
  LanguageInfo,
  ReasonUpdateParams,
} from 'types';
import formatHeaders from '../utils/headers';

/**
 * This class provides helpers over SALES BACK REST API
 * https://falbala-idefix-acc.azurewebsites.net/swagger/index.html.
 * It should be used as a singleton.
 * It should not hold data except for the base API URL.
 */
export class IvBack {
  // public to be  mocked for tests
  httpClient: ApisauceInstance;

  readonly baseURL: string | undefined;

  constructor({ baseURL }: { baseURL: string | undefined }) {
    this.baseURL = baseURL;
    // This section adds Cookies to Browser request Headers
    // This allows the API to obtain the Token containted in the 'httpOnly Cookies'
    this.httpClient = create({ baseURL, withCredentials: true });
  }

  /**
   * This method should be called when login form is submitted
   * It sets the httpOnly Cookies needed for all other API endpoints
   * These params should be differents on every client
   */
  login = (userName: string, password: string): Promise<ApiResponse<LoginResponse>> => {
    return this.httpClient.post('/Administration/User/Login', {
      userName,
      password,
    });
  };

  checkLogin = (): Promise<ApiResponse<LoginResponse>> => {
    return this.httpClient.get('/Administration/User/CheckLogin');
  };

  logout = (): Promise<ApiResponse<Response>> => {
    return this.httpClient.post('/Administration/User/Logout');
  };

  createUserApi = ({
    userName,
    firstName,
    lastName,
    email,
    groupName,
    profileID,
    trainTypeIDs,
  }: UserParams): Promise<ApiResponse<CreatedUser>> => {
    return this.httpClient.post('/Administration/User/CreateUser', {
      userName,
      firstName,
      lastName,
      email,
      groupName,
      profileID,
      trainTypeIDs,
    });
  };

  modifyUserApi = ({
    userName,
    firstName,
    lastName,
    email,
    groupName,
    profileID,
    trainTypeIDs,
    id,
  }: User): Promise<ApiResponse<UserModifyInfo>> => {
    return this.httpClient.put('/Administration/User/UpdateUser', {
      userName,
      firstName,
      lastName,
      email,
      groupName,
      profileID,
      trainTypeIDs,
      id,
    });
  };

  getCirculations = (Payload: GetCirculationFilters): Promise<ApiResponse<Circulations>> => {
    const { weekdays, ...rest } = Payload;
    return this.httpClient.get(
      '/Circulations/GetCirculationsByFilters',
      {
        ...rest,
        weekdays: weekdays?.length === 0 ? 0 : weekdays?.split('').join(','),
      },
      {
        paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
      }
    );
  };

  refreshCirculationList = (Payload: GetCirculationFilters): Promise<ApiResponse<Circulations>> => {
    const { weekdays, ...rest } = Payload;
    return this.httpClient.get(
      '/Circulations/GetRefreshedServices',
      {
        ...rest,
        weekdays: weekdays?.length === 0 ? 0 : weekdays?.split('').join(','),
      },
      {
        paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
      }
    );
  };

  exportCirculationList = (Payload: GetCirculationFilters, language: string): Promise<ApiResponse<ArrayBuffer>> => {
    const { weekdays, ...rest } = Payload;
    return this.httpClient.get(
      '/Circulations/ExportToExcel',
      {
        ...rest,
        weekdays: weekdays?.length === 0 ? 0 : weekdays?.split('').join(','),
      },
      {
        headers: formatHeaders({ language }),
        responseType: 'arraybuffer',
        paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
      }
    );
  };

  // Customers by station
  fetchCustomersByStation = (payload: FluxClientsFilters): Promise<ApiResponse<CustomersByStation>> => {
    return this.httpClient.get('/FluxClient/GetFluxClientStationByFilters', payload, {
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
    });
  };
  refreshCustomersByStation = (Payload: FluxClientsFilters): Promise<ApiResponse<CustomersByStation>> => {
    return this.httpClient.get('/FluxClient/GetRefreshedFluxClientStation', Payload, {
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
    });
  };
  exportCustomersByStation = (Payload: FluxClientsFilters): Promise<ApiResponse<ArrayBuffer>> => {
    return this.httpClient.get('/FluxClient/ExportFluxClientStation', Payload, {
      responseType: 'arraybuffer',
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
    });
  };

  // Customers by OD
  fetchCustomersByOD = (payload: FluxClientsFilters): Promise<ApiResponse<CustomersByOD>> => {
    return this.httpClient.get('/FluxClientOD/GetFluxClientODByFilters', payload, {
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
    });
  };
  refreshCustomersByOD = (Payload: FluxClientsFilters): Promise<ApiResponse<CustomersByOD>> => {
    return this.httpClient.get('/FluxClientOD/GetRefreshedFluxClientOD', Payload, {
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
    });
  };
  exportCustomersByOD = (Payload: FluxClientsFilters): Promise<ApiResponse<ArrayBuffer>> => {
    return this.httpClient.get('/FluxClientOD/ExportFluxClientOD', Payload, {
      responseType: 'arraybuffer',
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
    });
  };

  deleteUserApi = (id: string): Promise<ApiResponse<Response>> => {
    return this.httpClient.delete(`/Administration/User/DeleteUser/${id}`);
  };

  deleteProfilApi = (id: string): Promise<ApiResponse<Response>> => {
    return this.httpClient.delete(`/Administration/Profile/DeleteProfile/${id}`);
  };

  /**
   * This method should be called when we want user information
   */
  getUserInfo = (userName: string): Promise<ApiResponse<User>> => {
    return this.httpClient.get('/Administration/User/GetUserByUserName', {
      userName,
    });
  };

  getPublicStations = (): Promise<ApiResponse<PublicStation[]>> => {
    return this.httpClient.get('/Administration/Station/GetStations');
  };

  getReasons = (): Promise<ApiResponse<Reason[]>> => {
    return this.httpClient.get('/Administration/Reason/GetReasons');
  };

  getSituations = (): Promise<ApiResponse<Situation[]>> => {
    return this.httpClient.get('/Administration/Situation/GetSituations');
  };

  getServiceTypes = (): Promise<ApiResponse<ServiceType[]>> => {
    return this.httpClient.get('/Administration/ServiceType/GetServiceTypes');
  };

  getCommunicationsSituations = (): Promise<ApiResponse<Situation[]>> => {
    return this.httpClient.get('/CommunicationHistory/GetCommunicationsSituations');
  };

  getSituationCategory = (): Promise<ApiResponse<SituationForm[]>> => {
    return this.httpClient.get('/Administration/SituationCategory/GetSituationCategories');
  };

  getSituationsCategory = (): Promise<ApiResponse<Situations[]>> => {
    return this.httpClient.get('/Administration/Situation/GetSituationGroupByCategory');
  };

  getHelpInfos = (payload?: HelpInfosParams): Promise<ApiResponse<HelpInfos>> => {
    return this.httpClient.get('/Administration/StandardSentence/GetStandardSentencesByFilters', payload, {
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
    });
  };

  getCompensationSentences = (): Promise<ApiResponse<CompensationSentence[]>> => {
    return this.httpClient.get('/Administration/CompensationType/GetCompensationsType');
  };

  updateCompensationSentenceApi = (
    compensationSentenceToUpdate: CompensationSentenceParams
  ): Promise<ApiResponse<CompensationSentence>> => {
    return this.httpClient.patch(
      '/Administration/CompensationType/UpdateCompensationType',
      compensationSentenceToUpdate
    );
  };

  getStationsServed = (name: string, date: string): Promise<ApiResponse<StationsServed[]>> => {
    return this.httpClient.get(`/Operation/GetService/${name}/${date}`);
  };

  getUsersList = (payload?: UserFilterParams): Promise<ApiResponse<Users>> => {
    return this.httpClient.get('/Administration/User/GetUsersByFilters', payload, {
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
    });
  };
  exportUserList = (
    payload?: Omit<UserFilterParams, 'pageNumber' | 'maxResultPage'>
  ): Promise<ApiResponse<ArrayBuffer>> => {
    return this.httpClient.get('/Administration/User/ExportToExcel', payload, {
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
      responseType: 'arraybuffer',
    });
  };

  getProfilesList = (): Promise<ApiResponse<Profil[]>> => {
    return this.httpClient.get('/Administration/Profile/GetProfiles');
  };

  getTrainType = (): Promise<ApiResponse<UserInfo[]>> => {
    return this.httpClient.get('/Administration/User/GetTrainTypes');
  };

  createProfilApi = (Payload: Omit<ProfilParams, 'id'>): Promise<ApiResponse<Profil[]>> => {
    return this.httpClient.post('/Administration/Profile/CreateProfile', Payload);
  };

  modifyProfilApi = (Payload: ProfilParams): Promise<ApiResponse<Profil[]>> => {
    return this.httpClient.put('/Administration/Profile/UpdateProfile', Payload);
  };

  getGroupsList = (): Promise<ApiResponse<Profil[]>> => {
    return this.httpClient.get('/Administration/User/GetUserGroups');
  };

  getSections = (): Promise<ApiResponse<Section[]>> => {
    return this.httpClient.get('/Administration/Section/GetSections', undefined);
  };

  resetPassword = (email: string): Promise<ApiResponse<Response>> => {
    return this.httpClient.post('/Administration/User/ResetPassword', { email });
  };

  updatePassword = (updatePasswordParams: UpdatePasswordParams): Promise<ApiResponse<MinimalResponse>> => {
    return this.httpClient.put('/Administration/User/UpdateUserPassword', {
      ...updatePasswordParams,
    });
  };

  // Communication --------------------------------------------------------------------------

  saveCommunications = (values: SendCommunicationFormValues): Promise<ApiResponse<MinimalResponse>> => {
    return this.httpClient.post('/Communication/SaveCommunications', {
      ...values,
    });
  };

  getStationsTemplatesPreview = (
    values: SendCommunicationFormValues
  ): Promise<ApiResponse<StationsTemplatesPreview>> => {
    return this.httpClient.post('/Communication/PreviewTemplates', {
      ...values,
    });
  };

  getInitializeCommunication = (values: GetInitializeCommunication): Promise<ApiResponse<InitializeCommunication>> => {
    const { weekdays, ...rest } = values;
    return this.httpClient.get(
      '/Communication/InitializeCommunication',
      {
        ...rest,
        weekdays: weekdays?.length === 0 ? 0 : weekdays?.split('').join(','),
      },
      {
        paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
      }
    );
  };

  getHistoricCommunications = (Payload: CommunicationsHistoryForm): Promise<ApiResponse<HistoricCommunication>> => {
    return this.httpClient.get('/CommunicationHistory/GetCommunicationHistory', Payload, {
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
    });
  };

  exportHistoricCommunication = (
    Payload: CommunicationsHistoryForm,
    language: string
  ): Promise<ApiResponse<ArrayBuffer>> => {
    return this.httpClient.get('/CommunicationHistory/ExportToExcel', Payload, {
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
      headers: formatHeaders({ language }),
      responseType: 'arraybuffer',
    });
  };

  getCommunicationSenders = (): Promise<ApiResponse<string[]>> => {
    return this.httpClient.get('/CommunicationHistory/GetCommunicationSenders');
  };

  historicPendingCommunicationApi = (
    Payload: CommunicationsPendingHistoryForm
  ): Promise<ApiResponse<HistoricPendingCommunication>> => {
    return this.httpClient.get('/CommunicationHistory/GetPendingDifferedCommunications', Payload, {
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
    });
  };

  deleteHistoricPendingCommunication = (Payload: {
    communicationsIDs: number[];
  }): Promise<ApiResponse<HistoricCommunication>> => {
    return this.httpClient.put('/CommunicationHistory/DisableDifferedCommunication', Payload);
  };

  latestCommunication = (Payload: ServiceInfo): Promise<ApiResponse<LatestCommunication>> => {
    return this.httpClient.post('/Operation/GetLatestCommunication', Payload);
  };

  getServicesPassengersConcernedByCommunication = (
    payload: ServicesPassengersConcernedByCommunicationParams
  ): Promise<ApiResponse<ServicesPassengersConcernedByCommunicationResponse>> => {
    return this.httpClient.post('Communication/GetServicesPassengersConcernedByCommunication', payload);
  };

  // -------------------------------------------------------------------------------------

  createHelpInfoApi = (helpInfoToCreate: CreateHelpInfoParams): Promise<ApiResponse<HelpInfo>> => {
    return this.httpClient.post('Administration/StandardSentence/CreateStandardSentence', helpInfoToCreate);
  };

  updateHelpInfoApi = (helpInfoToUpdate: UpdateHelpInfoParams): Promise<ApiResponse<HelpInfo>> => {
    return this.httpClient.put('Administration/StandardSentence/UpdateStandardSentence', helpInfoToUpdate);
  };

  deleteHelpInfoApi = (id: string): Promise<ApiResponse<Response>> => {
    return this.httpClient.delete(`/Administration/StandardSentence/DeleteStandardSentence/${id}`);
  };

  createReasonApi = (reasonToCreate: ReasonCreationParams): Promise<ApiResponse<Reason>> => {
    return this.httpClient.post('/Administration/Reason/CreateReason', reasonToCreate);
  };

  updateReasonApi = (reasonToUpdate: ReasonUpdateParams): Promise<ApiResponse<Reason>> => {
    return this.httpClient.put('/Administration/Reason/UpdateReason', reasonToUpdate);
  };

  deleteReasonApi = (id: string): Promise<ApiResponse<Response>> => {
    return this.httpClient.delete(`/Administration/Reason/DeleteReason/${id}`);
  };

  createSituationApi = (situationToCreate: SituationForm): Promise<ApiResponse<SituationForm>> => {
    return this.httpClient.post('/Administration/Situation/CreateSituation', situationToCreate);
  };

  updateSituationApi = (situationToUpdate: SituationForm): Promise<ApiResponse<SituationForm>> => {
    return this.httpClient.put('/Administration/Situation/UpdateSituation', situationToUpdate);
  };

  deleteSituationApi = (id: string): Promise<ApiResponse<Response>> => {
    return this.httpClient.delete(`/Administration/Situation/DeleteSituation/${id}`);
  };

  getMask = (situationId: string): Promise<ApiResponse<Mask>> => {
    return this.httpClient.get(
      `Administration/SituationMessageMask/GetSituationMessageMaskById?situationId=${situationId}`
    );
  };

  getMasks = (): Promise<ApiResponse<Mask[]>> => {
    return this.httpClient.get('/Administration/SituationMessageMask/GetAllSituationsMessageMasks');
  };

  updateMaskApi = (maskToUpdate: FormData): Promise<ApiResponse<Mask>> => {
    return this.httpClient.put('/Administration/SituationMessageMask/CreateOrUpdateSituationMessageMask', maskToUpdate);
  };

  getMasksConfig = (): Promise<ApiResponse<MasksConfig>> => {
    return this.httpClient.get('/Administration/SituationMessageMask/GetSituationMessageMaskConfig');
  };

  // situationCategoryId is optional
  getSituationsMessageMaskByClientType = (payload?: {
    clientTypeId: number;
    situationCategoryId?: number;
  }): Promise<ApiResponse<Situations[]>> => {
    return this.httpClient.post('/Administration/SituationMessageMask/GetSituationsMessageMaskByClientType', payload);
  };

  getMessageMask = (situationId: string): Promise<ApiResponse<MessageMask>> => {
    return this.httpClient.get(
      `/Administration/CommunicationPageConfiguration/GetCommunicationPageConfigurationByID?SituationId=${situationId}`
    );
  };

  getMessageMasks = (): Promise<ApiResponse<MessageMask[]>> => {
    return this.httpClient.get('/Administration/CommunicationPageConfiguration/GetAllCommunicationPageConfigurations');
  };

  updateMessageMaskApi = (messageMaskToUpdate: MessageMask): Promise<ApiResponse<Response>> => {
    return this.httpClient.put(
      'Administration/CommunicationPageConfiguration/UpdateCommunicationPageConfiguration',
      messageMaskToUpdate
    );
  };

  getApiVersion = (): Promise<ApiResponse<ApiVersion>> => {
    return this.httpClient.get('/ApiVersion/GetApiVersion');
  };

  getProductCodes = (): Promise<ApiResponse<ProductCode[]>> => {
    return this.httpClient.get('/Administration/ProductCode/GetProductCodes');
  };

  deleteProductCodeApi = (id: string): Promise<ApiResponse<Response>> => {
    return this.httpClient.delete(`Administration/ProductCode/DeleteProductCode/${id}`);
  };

  createProductCodeApi = (productCodeToCreate: ProductCode): Promise<ApiResponse<ProductCode>> => {
    return this.httpClient.post('Administration/ProductCode/CreateProductCode', productCodeToCreate);
  };

  getTypologies = (): Promise<ApiResponse<Typology[]>> => {
    return this.httpClient.get('/Administration/PassengerTypology/GetPassengerTypologies');
  };

  createTypologyApi = (typologyToCreate: TypologyForm): Promise<ApiResponse<Typology>> => {
    return this.httpClient.post('/Administration/PassengerTypology/CreatePassengerTypology', typologyToCreate);
  };

  updateTypologyApi = (typologyToUpdate: TypologyForm): Promise<ApiResponse<Typology>> => {
    return this.httpClient.put('/Administration/PassengerTypology/UpdatePassengerTypology', typologyToUpdate);
  };

  deleteTypologyApi = (id: string): Promise<ApiResponse<Response>> => {
    return this.httpClient.delete(`/Administration/PassengerTypology/DeletePassengerTypology/${id}`);
  };

  getConflictTypes = (): Promise<ApiResponse<ConflictType[]>> => {
    return this.httpClient.get('/Conflict/GetAllConflictTypes');
  };

  getKeywords = (): Promise<ApiResponse<string[]>> => {
    return this.httpClient.get(`/Common/GetKeyWords`);
  };

  getSendStatus = (): Promise<ApiResponse<SendStatus[]>> => {
    return this.httpClient.get('/Common/GetSendStatus');
  };

  getEnabledTranslationLanguages = (): Promise<ApiResponse<LanguageInfo[]>> => {
    return this.httpClient.get('/Common/GetEnabledTranslationLanguages');
  };
}

let singleton: IvBack | undefined;

interface Config {
  baseURL: string;
}

function initAPI(config?: Config): IvBack {
  if (config && singleton && config.baseURL !== singleton.baseURL) {
    throw new Error(`IvBack API already defined with ${singleton.baseURL}`);
  }

  if (singleton) return singleton;

  if (config && config?.baseURL) {
    singleton = new IvBack({
      baseURL: config?.baseURL,
    });
  } else {
    // TODO: handle this case
  }

  return singleton as IvBack;
}

export default initAPI;
